import { useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { ArrowDownRight, ArrowUpRight, TrendingUp } from 'lucide-react'

export interface FlavorTrend {
  flavor: string
  recent_bags: number
  prior_bags: number
  recent_customers: number
  prior_customers: number
  is_seasonal: boolean
}

const WEEKS = 8
const LIST_LIMIT = 6
// Ignore flavors with too few bags in both periods to say anything.
const MIN_BAGS = 10

function pctChange(recent: number, prior: number): string {
  if (prior === 0) return 'new'
  const pct = Math.round(((recent - prior) / prior) * 100)
  return `${pct > 0 ? '+' : ''}${pct}%`
}

function TrendList({ title, rows, rising }: { title: string; rows: FlavorTrend[]; rising: boolean }) {
  const Icon = rising ? ArrowUpRight : ArrowDownRight
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium flex items-center gap-1.5 mb-1">
        <Icon className={rising ? 'w-4 h-4 text-green-600 dark:text-green-500' : 'w-4 h-4 text-red-600 dark:text-red-500'} />
        {title}
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">None right now.</p>
      ) : (
        <ul className="divide-y">
          {rows.map(r => {
            const diff = r.recent_bags - r.prior_bags
            return (
              <li key={r.flavor} className="py-1.5 text-sm">
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {r.flavor}
                    {r.is_seasonal && (
                      <span className="ml-1.5 text-[11px] font-normal text-muted-foreground border rounded px-1 py-px align-middle">
                        Seasonal
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 font-semibold tabular-nums">
                    {diff > 0 ? '+' : ''}
                    {diff.toLocaleString()} bags
                  </span>
                </div>
                <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
                  <span className="flex-1 tabular-nums">
                    {r.prior_bags.toLocaleString()} → {r.recent_bags.toLocaleString()} bags · {r.prior_customers} →{' '}
                    {r.recent_customers} customer{r.recent_customers === 1 ? '' : 's'}
                  </span>
                  <span className="shrink-0 tabular-nums">{pctChange(r.recent_bags, r.prior_bags)}</span>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// Flavors whose bag orders grew or shrank the most: last 8 weeks vs the 8
// weeks before. Small and Large count together.
export default function FlavorTrendsCard({ data, loading }: { data: FlavorTrend[]; loading: boolean }) {
  const { rising, falling, recentTotal, priorTotal } = useMemo(() => {
    const eligible = data.filter(r => Math.max(r.recent_bags, r.prior_bags) >= MIN_BAGS)
    const byChange = [...eligible].sort((a, b) => b.recent_bags - b.prior_bags - (a.recent_bags - a.prior_bags))
    return {
      rising: byChange.filter(r => r.recent_bags > r.prior_bags).slice(0, LIST_LIMIT),
      falling: byChange.filter(r => r.recent_bags < r.prior_bags).reverse().slice(0, LIST_LIMIT),
      recentTotal: data.reduce((sum, r) => sum + r.recent_bags, 0),
      priorTotal: data.reduce((sum, r) => sum + r.prior_bags, 0),
    }
  }, [data])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Rising and falling flavors
        </CardTitle>
        <CardDescription>
          Bags ordered in the last {WEEKS} weeks vs the {WEEKS} weeks before, Small and Large together
        </CardDescription>
        {!loading && priorTotal + recentTotal > 0 && (
          <p className="text-sm pt-1 text-muted-foreground">
            All flavors: {priorTotal.toLocaleString()} → {recentTotal.toLocaleString()} bags (
            {pctChange(recentTotal, priorTotal)})
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[260px] w-full" />
        ) : data.length === 0 ? (
          <p className="py-14 text-sm text-muted-foreground text-center px-4">
            No bag orders in the last {WEEKS * 2} weeks yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <TrendList title="Rising" rows={rising} rising />
            <TrendList title="Falling" rows={falling} rising={false} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
